import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Tonality } from '@dsny/dsny-component-library'
import { useTheme } from 'styled-components'
import {
  Add,
  Keyboardarrowdown,
  Search
} from '@dsny/dsny-component-library/icons'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { PlaylistSort } from 'constants/enums'
import {
  CompactCreateLabel,
  CreateIcon,
  FilterLabel,
  FullCreateLabel,
  LibraryCreateButton,
  SearchField,
  SearchToggle,
  SortMenu,
  SortOption,
  SortTrigger,
  Toolbar
} from './PlaylistLibraryToolbar.styles'

const SORT_OPTIONS = Object.values(PlaylistSort).filter(({ value }) => value)
const SEARCH_INPUT_STYLES = {
  height: 44,
  fontSize: 14,
  border: '1px solid #717070'
}
const SEARCH_WRAPPER_STYLES = {
  width: '100%',
  marginBottom: 0,
  paddingRight: 0
}

const PlaylistLibraryToolbar = ({
  searchToken,
  sort,
  onCreate,
  onSearchChange,
  onSearchSubmit,
  onSortChange
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const searchRef = useRef(null)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const currentSort = SORT_OPTIONS.find(({ value }) => value === sort)
  const searchLabel = t('playlist.search-playlists')

  function handleSearchToggle() {
    setIsSearchOpen((current) => !current)
    if (!isSearchOpen) {
      requestAnimationFrame(() => searchRef.current?.focus())
    }
  }

  function handleSearchSubmit(event) {
    event.preventDefault()
    onSearchSubmit()
  }

  return (
    <Toolbar>
      <SearchToggle
        aria-controls="playlist-library-search"
        aria-expanded={isSearchOpen}
        aria-label={searchLabel}
        type="button"
        onClick={handleSearchToggle}>
        <Search
          aria-hidden="true"
          color={theme.colors.neutralW90}
          height={16}
          width={16} />
      </SearchToggle>
      <SearchField $isOpen={isSearchOpen} onSubmit={handleSearchSubmit}>
        <Tonality.TextField
          hideInlineMessage
          hideLabel
          aria-label={searchLabel}
          id="playlist-library-search"
          inputRef={searchRef}
          inputStyle={SEARCH_INPUT_STYLES}
          labelText={searchLabel}
          leadingIconComponent={
            <Search color={theme.colors.neutralW90} height={16} width={16} />
          }
          name="playlist-search"
          placeholder={searchLabel}
          setting="player"
          type="search"
          value={searchToken}
          wrapperStyles={SEARCH_WRAPPER_STYLES}
          onChange={onSearchChange} />
      </SearchField>
      <FilterLabel>{t('home.filter-by')}</FilterLabel>
      <DropdownMenu.Root>
        <SortTrigger
          aria-label={`${t('home.filter-by')} ${t(
            currentSort?.display || PlaylistSort.newest.display
          )}`}
          type="button">
          {t(currentSort?.display || PlaylistSort.newest.display)}
          <Keyboardarrowdown aria-hidden="true" height={16} width={16} />
        </SortTrigger>
        <DropdownMenu.Portal>
          <SortMenu align="start" collisionPadding={8} sideOffset={5}>
            <DropdownMenu.RadioGroup value={sort} onValueChange={onSortChange}>
              {SORT_OPTIONS.map(({ display, value }) => (
                <SortOption key={value} value={value}>
                  {t(display)}
                </SortOption>
              ))}
            </DropdownMenu.RadioGroup>
          </SortMenu>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      <LibraryCreateButton
        setting="player"
        type="button"
        variant="primary"
        onClick={onCreate}>
        <CreateIcon aria-hidden="true">
          <Add height={16} width={16} />
        </CreateIcon>
        <FullCreateLabel>{t('playlist.library-create-playlist')}</FullCreateLabel>
        <CompactCreateLabel>{t('playlist.create-new')}</CompactCreateLabel>
      </LibraryCreateButton>
    </Toolbar>
  )
}

PlaylistLibraryToolbar.propTypes = {
  searchToken: PropTypes.string.isRequired,
  sort: PropTypes.string.isRequired,
  onCreate: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired
}

export default PlaylistLibraryToolbar
